
import axios from "axios";

// ============================================================
// CONFIGURATION API : LOCAL ET PRODUCTION
// ============================================================

const isLocal =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const API_URL = isLocal
  ? "http://localhost:8000"
  : "https://smart-hr.csi-digital-solutions.com/api";

const api = axios.create({
  baseURL: API_URL,
  // Ne pas définir Content-Type globalement.
  // Axios gère automatiquement JSON et FormData.
});

// ============================================================
// GESTION DU STOCKAGE JWT
// ============================================================

function getToken() {
  return localStorage.getItem("smarthr_token");
}

function getRefreshToken() {
  return localStorage.getItem("smarthr_refresh_token");
}

function setTokens(accessToken, refreshToken) {
  if (accessToken) {
    localStorage.setItem("smarthr_token", accessToken);
  }

  if (refreshToken) {
    localStorage.setItem("smarthr_refresh_token", refreshToken);
  }
}

function clearTokens() {
  localStorage.removeItem("smarthr_token");
  localStorage.removeItem("smarthr_refresh_token");
  localStorage.removeItem("smarthr_user");

  // Nettoyage des anciennes clés
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
}

// ============================================================
// REFRESH JWT
// ============================================================

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

function notifyTokenRefreshed(accessToken) {
  refreshSubscribers.forEach((callback) => {
    callback(accessToken);
  });

  refreshSubscribers = [];
}

function redirectToLogin() {
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

// ============================================================
// INTERCEPTEUR DES REQUÊTES
// ============================================================

api.interceptors.request.use(
  (config) => {
    const token = getToken();

    const method = config.method
      ? config.method.toUpperCase()
      : "GET";

    console.log(
      `[API] ${method} ${config.baseURL || ""}${config.url || ""}`
    );

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;

      console.log(
        `[API] JWT présent : ${token.substring(0, 20)}...`
      );
    } else {
      console.warn(
        `[API] Aucun JWT trouvé pour ${method} ${config.url}`
      );
    }

    return config;
  },
  (error) => {
    console.error("[API] Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// ============================================================
// INTERCEPTEUR DES RÉPONSES
// ============================================================

api.interceptors.response.use(
  (response) => {
    const method = response.config?.method
      ? response.config.method.toUpperCase()
      : "GET";

    console.log(
      `[API] ${method} ${response.config?.url} -> ${response.status}`
    );

    return response;
  },

  async (error) => {
    // --------------------------------------------------------
    // ERREUR RÉSEAU
    // --------------------------------------------------------

    if (!error.response) {
      console.error("[API] Network error:", error.message);
      return Promise.reject(error);
    }

    const status = error.response.status;
    const originalRequest = error.config;

    console.error(
      `[API] HTTP ${status} : ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`
    );

    console.error("[API] Response data:", error.response.data);

    // --------------------------------------------------------
    // 401 : TOKEN EXPIRÉ OU INVALIDE
    // --------------------------------------------------------

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        console.warn(
          "[API] 401 mais aucun refresh token disponible."
        );

        clearTokens();
        redirectToLogin();

        return Promise.reject(error);
      }

      // Une autre requête renouvelle déjà le token.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newAccessToken) => {
            if (!newAccessToken) {
              reject(error);
              return;
            }

            originalRequest.headers =
              originalRequest.headers || {};

            originalRequest.headers.Authorization =
              `Bearer ${newAccessToken}`;

            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log("[API] Tentative de refresh du JWT...");

        // Utiliser la même URL de base que l'API.
        // Ne pas utiliser l'instance api afin d'éviter
        // de réintercepter la requête de refresh.
        const refreshResponse = await axios.post(
          `${API_URL}/auth/refresh`,
          {
            refresh_token: refreshToken,
          }
        );

        const {
          access_token,
          refresh_token,
        } = refreshResponse.data;

        if (!access_token) {
          throw new Error(
            "Le serveur n'a pas renvoyé de nouvel access token."
          );
        }

        setTokens(access_token, refresh_token);

        console.log("[API] Refresh JWT réussi.");

        // Réveiller les requêtes en attente.
        notifyTokenRefreshed(access_token);

        originalRequest.headers =
          originalRequest.headers || {};

        originalRequest.headers.Authorization =
          `Bearer ${access_token}`;

        return api(originalRequest);
      } catch (refreshError) {
        console.error(
          "[API] Échec du refresh JWT :",
          refreshError
        );

        // Réveiller les requêtes en attente en cas d'échec.
        notifyTokenRefreshed(null);

        clearTokens();
        redirectToLogin();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // --------------------------------------------------------
    // 403 : PERMISSIONS INSUFFISANTES
    // --------------------------------------------------------

    if (status === 403) {
      console.warn(
        "[API] 403 Forbidden - permissions insuffisantes."
      );
    }

    return Promise.reject(error);
  }
);

export default api;